import { useMemo, type RefObject } from "react";
import type { Category, Offer, Product, WorkshopService } from "@/types";
import type { ProductStock } from "@/hooks/useCatalog";
import { resolvePrice } from "@/lib/posPricing";
import { formatMoney, formatQty } from "@/lib/format";

interface ProductBrowserProps {
  products: Product[];
  services: WorkshopService[];
  categories: Category[];
  offers: Offer[];
  customerType: string | null;
  branchId: string | null;
  stockByProduct: Record<string, ProductStock>;
  loading: boolean;
  error: string | null;
  tab: "products" | "services";
  onTabChange: (tab: "products" | "services") => void;
  search: string;
  onSearchChange: (value: string) => void;
  searchRef: RefObject<HTMLInputElement | null>;
  onAddProduct: (product: Product) => void;
  onAddService: (service: WorkshopService) => void;
  onRetry: () => void;
}

export default function ProductBrowser({
  products,
  services,
  categories,
  offers,
  customerType,
  branchId,
  stockByProduct,
  loading,
  error,
  tab,
  onTabChange,
  search,
  onSearchChange,
  searchRef,
  onAddProduct,
  onAddService,
  onRetry,
}: ProductBrowserProps) {
  const getStock = (productId: string): number => {
    const bucket = stockByProduct[productId];
    if (!bucket) return 0;
    if (branchId) return Number(bucket.byBranch[branchId] ?? 0);
    return bucket.total;
  };

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products
      .filter((product) => product.active !== false)
      .filter((product) => {
        if (!term) return true;
        return (
          product.name.toLowerCase().includes(term) ||
          (product.sku ?? "").toLowerCase().includes(term) ||
          (product.barcode ?? "").toLowerCase().includes(term) ||
          (product.model ?? "").toLowerCase().includes(term) ||
          (product.tire_size ?? "").toLowerCase().includes(term)
        );
      })
      .slice(0, 120);
  }, [products, search]);

  const filteredServices = useMemo(() => {
    const term = search.trim().toLowerCase();
    return services
      .filter((service) => service.active !== false)
      .filter((service) => {
        if (!term) return true;
        return (
          service.name.toLowerCase().includes(term) ||
          (service.category ?? "").toLowerCase().includes(term)
        );
      });
  }, [services, search]);

  const activeCategories = categories.filter((c) => c.active);

  return (
    <section className="flex min-h-0 flex-1 flex-col rounded-lg border border-background-200 bg-background-100">
      <div className="flex flex-col gap-3 border-b border-background-200 p-4">
        <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
          <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
            <i className="ri-barcode-line text-lg"></i>
          </span>
          <input
            ref={searchRef}
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Buscar o escanear producto (nombre, SKU, código de barras)..."
            className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-300"
          />
          {search ? (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="flex h-6 w-6 items-center justify-center rounded-md text-foreground-400 hover:bg-background-200"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
            <button
              type="button"
              onClick={() => onTabChange("products")}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                tab === "products"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              <i className="ri-shopping-bag-3-line text-base"></i>
              Productos
            </button>
            <button
              type="button"
              onClick={() => onTabChange("services")}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                tab === "services"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              <i className="ri-tools-line text-base"></i>
              Servicios del taller
            </button>
          </div>

          {tab === "products" && activeCategories.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {activeCategories.slice(0, 6).map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => onSearchChange(category.name)}
                  className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1 text-[11px] font-medium text-foreground-600 transition-colors hover:border-secondary-300 hover:bg-secondary-100 hover:text-secondary-900"
                >
                  {category.name}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="rkc-scroll-thin min-h-0 flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center gap-3 py-16 text-sm text-foreground-500">
            <i className="ri-loader-4-line animate-spin text-lg"></i>
            Cargando catálogo...
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-error-warning-line text-2xl"></i>
            </span>
            <p className="text-sm text-foreground-600">{error}</p>
            <button
              type="button"
              onClick={onRetry}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-refresh-line text-base"></i>
              Reintentar
            </button>
          </div>
        ) : tab === "products" ? (
          filteredProducts.length === 0 ? (
            <EmptyState icon="ri-search-eye-line" text="No hay productos que coincidan con la búsqueda." />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
              {filteredProducts.map((product) => {
                const price = resolvePrice(product, customerType, offers);
                const stock = getStock(product.id);
                const low = stock <= Number(product.min_stock ?? 0);
                return (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => onAddProduct(product)}
                    className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 text-left transition-colors hover:border-primary-300 hover:bg-primary-50"
                  >
                    <div className="relative h-24 w-full overflow-hidden bg-background-200">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="h-24 w-full object-cover object-top"
                        />
                      ) : (
                        <div className="flex h-24 w-full items-center justify-center text-foreground-300">
                          <i className="ri-image-2-line text-3xl"></i>
                        </div>
                      )}
                      <div className="absolute left-1.5 top-1.5 flex flex-col gap-1">
                        {price.offerApplied ? (
                          <span className="rounded-full bg-primary-500 px-2 py-0.5 text-[10px] font-bold text-background-50">
                            Oferta
                          </span>
                        ) : null}
                        {price.wholesale ? (
                          <span className="rounded-full bg-accent-500 px-2 py-0.5 text-[10px] font-bold text-background-50">
                            Mayorista
                          </span>
                        ) : null}
                      </div>
                      <div className="absolute right-1.5 top-1.5">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            stock <= 0
                              ? "bg-foreground-950/70 text-background-50"
                              : low
                                ? "bg-primary-100 text-primary-700"
                                : "bg-background-50/90 text-foreground-700"
                          }`}
                        >
                          {stock <= 0 ? "Agotado" : `${formatQty(stock)} ${product.unit ?? ""}`.trim()}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col gap-1 p-2.5">
                      <p className="line-clamp-2 text-xs font-semibold leading-snug text-foreground-900">
                        {product.name}
                      </p>
                      <p className="truncate text-[11px] text-foreground-400">
                        {product.sku || product.model || product.tire_size || ""}
                      </p>
                      <div className="mt-auto flex items-center justify-between gap-2 pt-1.5">
                        <span className="font-heading text-sm font-bold text-primary-600">
                          {formatMoney(price.final)}
                        </span>
                        {price.offerApplied ? (
                          <span className="text-[11px] text-foreground-400 line-through">
                            {formatMoney(price.base)}
                          </span>
                        ) : (
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-background-50 opacity-0 transition-opacity group-hover:opacity-100">
                            <i className="ri-add-line text-base"></i>
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )
        ) : filteredServices.length === 0 ? (
          <EmptyState icon="ri-tools-line" text="No hay servicios del taller disponibles." />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
            {filteredServices.map((service) => (
              <button
                key={service.id}
                type="button"
                onClick={() => onAddService(service)}
                className="group flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-4 text-left transition-colors hover:border-accent-300 hover:bg-accent-50"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-tools-line text-xl"></i>
                </span>
                <p className="line-clamp-2 text-xs font-semibold leading-snug text-foreground-900">
                  {service.name}
                </p>
                <p className="truncate text-[11px] text-foreground-400">
                  {service.category || "Servicio"}
                  {service.duration_minutes ? ` · ${service.duration_minutes} min` : ""}
                </p>
                <span className="font-heading text-sm font-bold text-accent-700">
                  {formatMoney(service.price)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function EmptyState({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-400">
        <i className={`${icon} text-2xl`}></i>
      </span>
      <p className="text-sm text-foreground-500">{text}</p>
    </div>
  );
}